/*
 * What a Done, a move back to the inbox, or the Undo of either says (LEA-225). Done also closes the
 * agent's open draft and going back reopens it, so the sentence says both, but only to a viewer the
 * server told about the agent: everyone else hears only where the conversations went.
 */
import type { InboxDoneResult } from "../../src/inbox-v2/contract.ts";

const plural = (n: number, word: string) =>
  `${n.toLocaleString("en-GB")} ${word}${n === 1 ? "" : "s"}`;

/** The agent's part of one gesture, gathered over the one or two calls an Undo makes. */
export function mergeDone(results: readonly InboxDoneResult[]): InboxDoneResult {
  return {
    previous: results.flatMap((result) => result.previous),
    closures: results.flatMap((result) => result.closures ?? []),
    reopened: results.flatMap((result) => result.reopened ?? []),
    kept: results.flatMap((result) => result.kept ?? []),
  };
}

/** "Marked done." for one, "3 conversations marked done." for several; the same for going back. */
function where(count: number, putAway: boolean): string {
  if (count === 1) return putAway ? "Marked done." : "Back in the inbox.";
  return `${plural(count, "conversation")} ${putAway ? "marked done" : "back in the inbox"}.`;
}

/** How many drafts a gesture closed or reopened: "Draft closed." for one conversation. */
function drafts(count: number, changed: number, verb: string): string | null {
  if (changed === 0) return null;
  return count === 1 ? `Draft ${verb}.` : `${plural(changed, "draft")} ${verb}.`;
}

/** Drafts a newer lead message replaced, which going back leaves standing. */
function newerDrafts(count: number, newer: number): string | null {
  if (newer === 0) return null;
  if (count === 1) return "It had a new message, so its new draft stands.";
  return `${newer} had a new message, so ${newer === 1 ? "its new draft stands" : "their new drafts stand"}.`;
}

export function doneToast(count: number, putAway: boolean, result: InboxDoneResult): string {
  const newer = (result.kept ?? []).filter((row) => row.reason === "new_message").length;
  const parts = putAway
    ? [where(count, true), drafts(count, result.closures?.length ?? 0, "closed")]
    : [
        where(count, false),
        drafts(count, result.reopened?.length ?? 0, "reopened"),
        newerDrafts(count, newer),
      ];
  return parts.filter((part) => part !== null).join(" ");
}
